import { useEffect, useState, type JSX } from 'react';
import type { CharacterSheet, SheetTemplate } from '@roll1/shared';
import type { TranslationKeys } from '../../../i18n/types.js';
import ConfirmDialog from '../../ConfirmDialog';
import PublicFieldsEditor from '../../settings/PublicFieldsEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { FIELD_CLASS } from '../../ui/field';
import { SECTION_HEADER_CLASS } from '../../ui/section-header';
import { buttonVariants } from '../../ui/button';
import { ListChecks, Trash2 } from 'lucide-react';
import { IconButton } from '../../ui/icon-button';
import { RowMenu } from '../../ui/row-menu';

/**
 * T-19 (owner decision 18.10 n.8): a sheet is managed in Studio — owner, group, public fields, deletion.
 * The table only shows sheets. Native controls with real labels (accessible names «Owner», «Group»).
 */

export interface SheetManageData {
  /** The campaign's players (not the GM): the choices of the owner select. */
  readonly players: readonly string[];
  /** The groups sheets already have: suggestions for the group field. */
  readonly groups: readonly string[];
  readonly template: SheetTemplate | null;
}

export interface SheetManageBlockProps {
  readonly sheet: CharacterSheet;
  readonly manage: SheetManageData;
  readonly send: (msg: unknown) => void;
  readonly t: (key: keyof TranslationKeys) => string;
}

export function SheetManageBlock({ sheet, manage, send, t }: SheetManageBlockProps): JSX.Element {
  const [group, setGroup] = useState<string>(sheet.group ?? '');
  // Only what the GM typed is sent: an outside change (a rename of the group, another tab) is taken into the field
  // and never written back by a blur.
  const [typed, setTyped] = useState<boolean>(false);
  useEffect(() => {
    setGroup(sheet.group ?? '');
    setTyped(false);
  }, [sheet.group, sheet.id]);
  const [fieldsOpen, setFieldsOpen] = useState<boolean>(false);
  const [askDelete, setAskDelete] = useState<boolean>(false);
  // Giving the sheet to a player shows them all of it: asked first (the select stays on the old owner until then).
  const [askOwner, setAskOwner] = useState<string | null>(null);
  const idBase = `sheet-manage-${sheet.id}`;

  const commitGroup = (value: string): void => {
    const next = value.trim();
    setTyped(false);
    if (next === (sheet.group ?? '')) return;
    send({ type: 'sheet:set-group', sheetId: sheet.id, group: next });
  };

  const field = FIELD_CLASS;
  const label = SECTION_HEADER_CLASS;

  return (
    <section className="sheet-manage flex flex-col gap-2 rounded-md border border-line px-2.5 pb-2" data-testid="sheet-manage">
      {/* The section's header line (group T, 32 px): its words and its ≤2 icons — «Публичные поля…» and ⋯ (delete). */}
      <div className="pane-section-head flex items-center gap-1.5" data-section-head="">
        <h4 className={`${label} min-w-0 flex-1 truncate`}>{t('studio.sheet.manage')}</h4>
        {manage.template !== null ? (
          <IconButton
            label={t('studio.sheet.publicFields')}
            icon={<ListChecks size={14} aria-hidden="true" />}
            data-testid="sheet-public-fields"
            onClick={(): void => setFieldsOpen(true)}
          />
        ) : null}
        {/* Sidebar polish П5: no red button — «Удалить лист…» is in the ⋯, the block asks first. */}
        <RowMenu
          size="md"
          testid="sheet-manage-more"
          items={[{ label: t('studio.sheet.delete'), icon: Trash2, danger: true, testid: 'sheet-delete', onSelect: (): void => setAskDelete(true) }]}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${idBase}-owner`} className="text-meta text-muted-foreground">{t('studio.sheet.owner')}</label>
          <select
            id={`${idBase}-owner`}
            className={field}
            value={sheet.ownerName ?? ''}
            onChange={(e): void => {
              if (e.target.value === '') send({ type: 'sheet:assign-owner', sheetId: sheet.id, ownerName: null });
              else if (manage.players.includes(e.target.value)) setAskOwner(e.target.value);
              else send({ type: 'sheet:assign-owner', sheetId: sheet.id, ownerName: e.target.value });
            }}
          >
            <option value="">{t('studio.sheet.noOwner')}</option>
            {sheet.ownerName !== null && !manage.players.includes(sheet.ownerName) ? (
              <option value={sheet.ownerName}>{sheet.ownerName}</option>
            ) : null}
            {manage.players.map((name: string): JSX.Element => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${idBase}-group`} className="text-meta text-muted-foreground">{t('studio.sheet.group')}</label>
          <input
            id={`${idBase}-group`}
            type="text"
            maxLength={50}
            className={field}
            value={group}
            onChange={(e): void => { setGroup(e.target.value); setTyped(true); }}
            onBlur={(): void => { if (typed) commitGroup(group); }}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitGroup(group);
              }
            }}
          />
        </div>
      </div>
      {manage.groups.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {manage.groups.map((name: string): JSX.Element => (
            <button
              key={name}
              type="button"
              className="rounded-full border border-line px-2 text-pill text-muted-foreground hover:border-hl/50 hover:text-fg"
              onClick={(): void => {
                setGroup(name);
                commitGroup(name);
              }}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}

      <Dialog open={fieldsOpen} onOpenChange={setFieldsOpen}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>{t('studio.sheet.publicFields')} — {sheet.name}</DialogTitle>
          </DialogHeader>
          {manage.template !== null ? (
            // key: PublicFieldsEditor seeds its checkbox state from sheet.publicFields once on mount.
            <PublicFieldsEditor key={sheet.id} sheet={sheet} template={manage.template} />
          ) : null}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={askOwner !== null}
        onOpenChange={(open): void => { if (!open) setAskOwner(null); }}
        title={t('studio.sheet.ownerConfirmTitle').replace('{name}', sheet.name).replace('{player}', askOwner ?? '')}
        description={t('studio.sheet.ownerConfirmDesc')}
        confirmLabel={t('studio.sheet.ownerConfirm')}
        onConfirm={(): void => { if (askOwner !== null) send({ type: 'sheet:assign-owner', sheetId: sheet.id, ownerName: askOwner }); }}
        onCancel={(): void => setAskOwner(null)}
      />
      <ConfirmDialog
        open={askDelete}
        onOpenChange={setAskDelete}
        title={t('characters.deleteConfirmTitle')}
        tone="danger"
        description={t('characters.deleteConfirmDesc').replace('{name}', sheet.name)}
        confirmLabel={t('common.delete')}
        onConfirm={(): void => send({ type: 'sheet:delete', sheetId: sheet.id })}
        onCancel={(): void => setAskDelete(false)}
      />
    </section>
  );
}

export default SheetManageBlock;
